import React from "react";
import { Spin, Empty, Image } from "antd";
import {
  CheckCircleOutlined,
  LoadingOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { FilePreviewModal } from "../../../components/FilePreviewModal";
import type { OrderAttachmentConfig } from "@ifa/utils";
import pdfIcon from "../../../assets/img/pdf.png";
import excelIcon from "../../../assets/img/excel.png";
import wordIcon from "../../../assets/img/word.png";
import styles from "./AttachmentsView.module.css";

interface AttachmentsViewProps {
  attachmentConfig: OrderAttachmentConfig[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  loading: boolean;
  previewOpen: boolean;
  previewFile?: { url: string; name: string };
  onPreviewOpenChange: (open: boolean) => void;
  onPreviewFileChange: (file: { url: string; name: string }) => void;
}

export function AttachmentsView({
  attachmentConfig,
  selectedCategory,
  onCategoryChange,
  loading,
  previewOpen,
  previewFile,
  onPreviewOpenChange,
  onPreviewFileChange,
}: AttachmentsViewProps) {
  // Helper to check if category is complete
  const isCategoryComplete = (category: OrderAttachmentConfig) => {
    if (!category.required) {
      const hasFiles = category.items?.some(
        (item) => item.files && item.files.length > 0
      );
      return hasFiles;
    }
    const allRequiredItemsFilled = category.items?.every((item) => {
      if (!item.required) return true;
      const validFiles = item.files && item.files.length > 0;
      return validFiles;
    });
    return allRequiredItemsFilled;
  };

  // Handle file preview
  const handlePreview = (file: any) => {
    const filePath = file.ossPath || file.path || file.response || file.url;
    if (!filePath) {
      return;
    }
    onPreviewFileChange({ url: filePath, name: file.name || "unnamed" });
    onPreviewOpenChange(true);
  };

  const renderSectionTitle = (title: string) => (
    <div style={{ marginBottom: 16, marginTop: 0 }}>
      <span style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
        {title}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div
        className={styles["detail-content"]}
        style={{ display: "flex", justifyContent: "center", paddingTop: 50 }}
      >
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          tip="載入附件配置中..."
        />
      </div>
    );
  }

  const activeCategory = attachmentConfig.find(
    (c) => c.type === selectedCategory
  );

  return (
    <div
      className={styles["detail-content"]}
      style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}
    >
      {/* Left Sidebar */}
      <div className={styles["sidebar"]}>
        {attachmentConfig.map((cat) => {
          const isComplete = isCategoryComplete(cat);
          const isSelected = selectedCategory === cat.type;

          return (
            <div
              key={cat.type}
              onClick={() => onCategoryChange(cat.type || "")}
              className={`${styles["sidebar-item"]} ${
                isSelected ? styles["active"] : ""
              }`}
            >
              <span
                style={{
                  flex: 1,
                  width: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {cat.name}
              </span>
              {isComplete && (
                <CheckCircleOutlined
                  style={{ color: "#00C7B6", fontSize: 16 }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Right Content */}
      <div className={styles["content-area"]}>
        {activeCategory ? (
          <div>
            {renderSectionTitle(activeCategory.name || "附件")}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 32 }}>
              {activeCategory.items?.map((item, index) => {
                const files = item.files || [];
                const fileUrl =
                  files.length > 0
                    ? typeof files[0] === "string"
                      ? files[0]
                      : files[0].url || files[0].path
                    : null;

                // Construct a file object compatible with handlePreview
                const rawFile = files.length > 0 ? files[0] : null;
                const fileObj = rawFile
                  ? typeof rawFile === "string"
                    ? {
                        url: rawFile,
                        path: rawFile,
                        name: rawFile.split("/").pop(),
                        status: "done",
                        uid: String(index),
                      }
                    : rawFile
                  : null;

                const fileName =
                  files.length > 0 &&
                  typeof files[0] === "object" &&
                  files[0].name
                    ? files[0].name
                    : fileUrl
                    ? fileUrl.split("/").pop()
                    : "";
                const lowerName = fileName.toLowerCase();
                const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(
                  lowerName
                );
                let iconSrc = null;

                if (fileUrl) {
                  if (lowerName.endsWith(".pdf")) {
                    iconSrc = pdfIcon;
                  } else if (/\.(xls|xlsx|csv)$/.test(lowerName)) {
                    iconSrc = excelIcon;
                  } else if (/\.(doc|docx)$/.test(lowerName)) {
                    iconSrc = wordIcon;
                  }
                }

                return (
                  <div key={index} style={{ textAlign: "center" }}>
                    <div
                      className={styles["file-thumbnail"]}
                      onClick={() => {
                        if (fileUrl && fileObj) {
                          handlePreview(fileObj);
                        }
                      }}
                    >
                      {fileUrl ? (
                        iconSrc ? (
                          <div className={styles["icon-wrapper"]}>
                            <img
                              src={iconSrc}
                              alt={item.name}
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "contain",
                              }}
                            />
                          </div>
                        ) : isImage ? (
                          <Image
                            src={fileUrl}
                            alt={item.name}
                            style={{
                              width: 160,
                              height: 110,
                              objectFit: "cover",
                            }}
                            fallback="https://via.placeholder.com/160x110?text=Load+Failed"
                            preview={false}
                          />
                        ) : (
                          <div className={styles["generic-file-icon"]}>
                            <FileOutlined
                              style={{ fontSize: 40, color: "#999" }}
                            />
                            <span style={{ fontSize: 12, color: "#999" }}>
                              {lowerName.split(".").pop()?.toUpperCase() ||
                                "FILE"}
                            </span>
                          </div>
                        )
                      ) : (
                        <div style={{ color: "#ccc", fontSize: 12 }}>
                          未上載
                        </div>
                      )}
                    </div>
                    <div style={{ color: "#666", fontSize: 14 }}>
                      {item.name}
                    </div>
                  </div>
                );
              })}

              {(!activeCategory.items || activeCategory.items.length === 0) && (
                <Empty description="暫無附件項" />
              )}
            </div>
          </div>
        ) : (
          <Empty description="請選擇附件分類" style={{ marginTop: 100 }} />
        )}

        {/* File Preview Modal */}
        <FilePreviewModal
          open={previewOpen}
          onClose={() => onPreviewOpenChange(false)}
          fileUrl={previewFile?.url}
          fileName={previewFile?.name}
        />
      </div>
    </div>
  );
}
